import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { QuickstartPill } from "@ui/pages/program-builder/quickstart/quickstart-pill";
import { QuickstartStep } from "@ui/pages/program-builder/quickstart/quickstart-step";
import { QuickstartStepDefinition, QuickstartStepState } from "@ui/pages/program-builder/quickstart/quickstart-types";
import { loadQuickstartVisibility, saveQuickstartVisibility } from "@ui/pages/program-builder/quickstart/quickstart-storage";
import { hideAlternativeHalo } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import { useI18n } from "@core/i18n/i18n";
import { mapStepIdToGuideName, trackStartQuickstart } from "@ui/pages/program-builder/quickstart/quickstart-analytics";

interface QuickstartPopoverProps {
  steps: QuickstartStepDefinition[];
  onClose?: () => void;
}

export const QuickstartPopover: React.FC<QuickstartPopoverProps> = ({ steps, onClose }) => {
  const { format } = useI18n();
  const initial = loadQuickstartVisibility();
  const [open, setOpen] = useState(initial.open);
  const [minimized, setMinimized] = useState(initial.minimized);

  const closeAndMinimize = useCallback(() => {
    hideAlternativeHalo();
    setOpen(false);
    setMinimized(true);
  }, []);

  const restore = useCallback(() => {
    setOpen(true);
    setMinimized(false);
  }, []);

  const toggle = useCallback(() => {
    hideAlternativeHalo();
    if (open && !minimized) {
      closeAndMinimize();
    } else {
      restore();
    }
  }, [open, minimized, closeAndMinimize, restore]);

  useEffect(() => {
    saveQuickstartVisibility({ open, minimized });
  }, [open, minimized]);
  
  useEffect(() => {
    if (open && !minimized) {
      hideAlternativeHalo();
    }
  }, [open, minimized]);

  const orderedSteps = useMemo(() => steps, [steps]);

  const resolveState = (s: QuickstartStepDefinition): QuickstartStepState => {
    if (s.computeState) return s.computeState();
  return "active";
  };

  const allCompleted = useMemo(
    () =>
      orderedSteps.every((s) => {
        const st = s.computeState ? s.computeState() : "active";
        return st === "completed" || st === "completed-disabled";
      }),
    [orderedSteps],
  );

  const autoMinimizedRef = useRef(false);
  useEffect(() => {
    if (allCompleted && !autoMinimizedRef.current) {
      setOpen(false);
      setMinimized(true);
      autoMinimizedRef.current = true;
    }
  }, [allCompleted]);

  const content = (
    <Container
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
  transition={{ duration: 0.25, ease: "easeOut" }}
      data-testid="quickstart-popover"
    >
      <Header>
  <Title>{format("programBuilder.quickstart.title")}</Title>
        <CloseBtn aria-label="close" onClick={() => { closeAndMinimize(); onClose?.(); }}>×</CloseBtn>
      </Header>
  <Intro>{format("programBuilder.quickstart.intro")}</Intro>
      <StepsList>
        {orderedSteps.map((s) => {
          const wrapped: QuickstartStepDefinition = {
            ...s,
            onClick: () => {
              closeAndMinimize();
              const guideName = mapStepIdToGuideName(s.id);
              if (guideName) {
                trackStartQuickstart(guideName);
              }
              s.onClick?.();
            },
          };
          return (
            <QuickstartStep key={s.id} step={wrapped} state={resolveState(s)} />
          );
        })}
      </StepsList>
    </Container>
  );

  return (
    <Wrapper>
      <AnimatePresence initial={false}>
        {open && !minimized && content}
      </AnimatePresence>
  <QuickstartPill onClick={toggle} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  pointer-events: none;
  > * { pointer-events: auto; }
`;

const Container = styled(motion.div)`
  width: 340px;
  max-height: 395px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  padding: 12px 12px 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: inherit;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #14202c;
`;

const Intro = styled.p`
  color: #5a6472;
  font-size: 16px;
`;

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  color: #5a6472;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 8px;
  &:hover { background: rgba(0,0,0,0.06); }
`;

const StepsList = styled.div`
  overflow-y: auto;
  padding: 4px 0 8px 0;
  display: flex;
  flex-direction: column;
`;
